import React from "react";
import { Map } from "immutable";
import cx from "classnames";

import AppConstants from "../../constants/AppConstants";
import TransformerStore from "../../stores/TransformerStore";
import TransformActions from "../../actions/TransformActions";
import Graphic from "./Graphic";
import Gallery from "./Gallery";
import SocialGrid from "./SocialGrid";
import SVG from "./SVG";
import Icon from "./Icon";
import Text from "./Text";
import Simple from "./Simple";
import Line from "./Line";
import Video from "./Video";
import Lottie from "./Lottie";
import IFrame from "./IFrame";
import Button from "./Button";
import FormField from "./FormField/FormField";
import { isFormFieldElementType } from "../../constants/FormFieldConstants.mjs";
import StyleUtils from "../../utils/StyleUtils";
import ElementUtils from "../../utils/ElementUtils";
import { gatherProps, getProp, inheritProp } from "../../utils/Merge/MergeUtils.mjs";
import PROPERTY_MAPS from "../../constants/PropertyMaps";
import { OPACITY, BLUR, HOVER_OPACITY, HOVER_BLUR } from "@/constants/MergeConstants";
import DataTypes from "@/constants/DataTypes";
import { ELEMENTS } from "@/constants/EntityConstants";
import BlockUtils from "@/utils/BlockUtils";
import { hasBackdropFilter } from "@/utils/Element/FilterUtils";

const { ELEMENT_POSITION } = PROPERTY_MAPS;
const { LAYOUT_MOBILE, SYNCED_PROPS } = AppConstants;
const ELEM_PROP_MAP = [...ELEMENT_POSITION, OPACITY];

const getBorderStyle = (elemProps) => {
  const radius = elemProps.getIn(["border", "rad"]);
  const addPx = (value) => `${value}px`;

  return radius === AppConstants.BORDER_CIRCLE_RAD
    ? {
        borderRadius: AppConstants.BORDER_CIRCLE_RAD,
        overflow: "hidden",
      }
    : radius !== undefined
      ? {
          borderRadius: String(radius).split(" ").map(addPx).join(" "),
          overflow: "hidden",
        }
      : {};
};

const getShadowStyle = (type, content, elemProps, theme) => {
  const hasBorderRadius = StyleUtils.hasBorderRadius(elemProps);
  const hasPNG = ElementUtils.hasPNG(content);
  const isSimpleGallery = ElementUtils.isSimpleGallery(type, content);

  const shadowType =
    AppConstants.ELEMENT_TYPE_GRAPHIC && !hasBorderRadius && hasPNG
      ? "png"
      : [
            AppConstants.ELEMENT_TYPE_SIMPLE,
            AppConstants.ELEMENT_TYPE_GRAPHIC,
            AppConstants.ELEMENT_TYPE_VIDEO,
            AppConstants.ELEMENT_TYPE_LOTTIE,
            AppConstants.ELEMENT_TYPE_BUTTON,
          ].includes(type) ||
          (isSimpleGallery && hasBorderRadius)
        ? "box"
        : "";

  return {
    box: () =>
      StyleUtils.getShadowStyle(
        elemProps,
        theme,
        type === AppConstants.ELEMENT_TYPE_BUTTON ? type : undefined,
        type === AppConstants.ELEMENT_TYPE_BUTTON,
      ),
    png: () => StyleUtils.getShadowStyle(elemProps, theme, "png"),
    "": () => {},
  }[shadowType]();
};

const isSelected = (element, layout) => element.getIn(["selected", "layout"]) === layout;

const getAdditionalElementStyles = (type, content, elemProps, theme) => ({
  ...(!isFormFieldElementType(type) && getBorderStyle(elemProps)),
  ...getShadowStyle(type, content, elemProps, theme),
});

class Element extends React.Component {
  constructor(props) {
    super(props);

    const { element, layout } = props;
    const state = { h: 0, w: 0, x: 0, y: 0, a: 0, o: 100 };
    const elemProps = gatherProps(element, layout, ELEM_PROP_MAP);

    state.x = elemProps.get("x");
    state.y = elemProps.get("y");
    state.w = elemProps.get("w");
    state.h = elemProps.get("h");
    state.a = elemProps.get("a");
    state.o = elemProps.get("o", 100);
    state.element = element;

    this.state = state;
  }

  // Called once after initial rendering
  componentDidMount() {
    if (isSelected(this.props.element, this.props.layout)) {
      this.transformUnsubscribe = TransformerStore.listen(this._onTransform);
      TransformActions.ready({ layout: this.props.layout });
    }
  }

  // Called after a component has been updated and re-rendered
  componentDidUpdate(prevProps) {
    const isElementSelected = isSelected(this.props.element, this.props.layout);
    const contained = this.props.element.get("pId");
    const isParentSelected = contained ? isSelected(this.props.parent, this.props.layout) : false;

    const addListener = (isElementSelected || isParentSelected) && !this.transformUnsubscribe;
    const removeListener = !isElementSelected && !isParentSelected && this.transformUnsubscribe;

    if (addListener) {
      this.transformUnsubscribe = TransformerStore.listen(this._onTransform);
    } else if (removeListener) {
      if (this.transformUnsubscribe) {
        this.transformUnsubscribe();
        this.transformUnsubscribe = null;
      }
    }
  }

  static getDerivedStateFromProps(props, state) {
    const { element, layout } = props;

    if (props.element !== state.element && !TransformerStore.getTransformingStatus()) {
      const elemProps = gatherProps(element, layout, ELEM_PROP_MAP);

      return {
        x: elemProps.get("x"),
        y: elemProps.get("y"),
        w: elemProps.get("w"),
        h: elemProps.get("h"),
        a: elemProps.get("a"),
        o: elemProps.get("o", 100),
        element,
      };
    }

    return null;
  }

  // Called before component is removed from DOM, perform cleanup here
  componentWillUnmount() {
    if (this.transformUnsubscribe) {
      this.transformUnsubscribe();
      this.transformUnsubscribe = null;
    }
  }

  _onTransform = () => {
    const { id, element, layout, parent } = this.props;
    const position = TransformerStore.getElementPosition(id);
    if (!position) return;

    // remove container rotation from position when element is contained and directly transformed
    if (element.get("pId") && parent && isSelected(element, layout)) {
      position.a -= getProp(parent, layout, "a");
    }

    this.setState(position);
  };

  _renderGroupChildren() {
    const { element: elem, id, ...restProps } = this.props;

    return elem
      .get("elementOrder")
      .filter((childId) =>
        BlockUtils.isVisibleInLayout(elem.getIn([ELEMENTS, childId]), this.props.layout),
      )
      .reverse()
      .map((childId) => (
        <Element
          {...restProps}
          key={`${id}.${ELEMENTS}.${childId}`}
          element={elem.getIn([ELEMENTS, childId])}
          id={`${id}.${ELEMENTS}.${childId}`}
          parent={elem}
        />
      ));
  }

  render() {
    const { element: elem, layout, id, theme, designContext, buttonState, mode } = this.props;
    let elemProps = elem.get(layout);

    const propOverrides = { designContext };
    if (layout === LAYOUT_MOBILE) {
      elemProps = elemProps.mergeDeep(
        gatherProps(elem, LAYOUT_MOBILE, elem.get(SYNCED_PROPS, []), true),
      );
    }
    elemProps = elemProps.merge(propOverrides);
    const type = elem.get("type");
    const isButton = type === AppConstants.ELEMENT_TYPE_BUTTON;
    const isButtonHover = isButton && buttonState === AppConstants.BUTTON_STATE_HOVER;
    const content = elem.get("content");

    // Add hover class if button state is set to hover
    const classNames = ["si-element", { hover: isButtonHover }];

    const isChildElement = !!elem.get("pid", false);

    const defaultStyle = {
      position: isChildElement ? "relative" : "absolute",
      width: isChildElement ? "auto" : this.state.w,
      height: isChildElement ? "fit-content" : this.state.h,
      top: isChildElement ? 0 : this.state.y,
      left: isChildElement ? 0 : this.state.x,
      transform: `rotate(${this.state.a}deg)`,
    };

    const baseOpacity = Number(elemProps.get(OPACITY, 100)) / 100;
    const opacity = isButtonHover
      ? Number(inheritProp(elem, layout, HOVER_OPACITY, theme) ?? elemProps.get(OPACITY, 100)) / 100
      : baseOpacity;

    const hasBackdropFilterData = hasBackdropFilter(elemProps.get("backdropFilter"));

    if (!hasBackdropFilterData) {
      defaultStyle.opacity = opacity;
    }

    const isIconOrSvg =
      type === AppConstants.ELEMENT_TYPE_ICON || type === AppConstants.ELEMENT_TYPE_SVG;

    if (!isIconOrSvg && !hasBackdropFilterData) {
      const baseBlur = isButton
        ? Number(inheritProp(elem, layout, BLUR, theme) ?? 0)
        : Number(elemProps.get(BLUR, 0) ?? 0);
      const blur = isButtonHover
        ? Number(inheritProp(elem, layout, HOVER_BLUR, theme) ?? baseBlur)
        : baseBlur;
      if (blur > 0) {
        defaultStyle.filter = defaultStyle.filter
          ? `${defaultStyle.filter} blur(${blur}px)`
          : `blur(${blur}px)`;
      }
    }

    if (isButton) {
      defaultStyle.minHeight = "fit-content";
    }

    const style = {
      ...defaultStyle,
      ...getAdditionalElementStyles(
        type,
        content,
        isButtonHover ? elemProps.mergeDeep(elemProps.get("hover", Map())) : elemProps,
        theme,
      ),
    };

    const elemStyle = elem.get("style");
    const elemSelected = isSelected(this.props.element, this.props.layout);
    const isEditing = elemSelected && elem.getIn(["selected", "edit"], false);

    if (isEditing && !isButton) {
      style.display = "none";
    }

    let view = null;

    switch (type) {
      case AppConstants.ELEMENT_TYPE_TEXT:
      case AppConstants.ELEMENT_TYPE_HTML_TEXT:
        view = (
          <Text
            id={id}
            content={content}
            elem={elem}
            theme={theme}
            layout={layout}
            isChildElement={isChildElement}
          />
        );
        break;

      case AppConstants.ELEMENT_TYPE_GRAPHIC:
        view = (
          <Graphic
            content={content}
            elemProps={elemProps}
            id={id}
            theme={theme}
            layout={layout}
            mode={mode}
            inheritShadow={style?.boxShadow && style.boxShadow.startsWith("inset")}
          />
        );
        break;

      case AppConstants.ELEMENT_TYPE_GALLERY:
        view = (
          <Gallery id={id} content={content} elemProps={elemProps} theme={theme} layout={layout} />
        );
        break;

      case AppConstants.ELEMENT_TYPE_SOCIAL_GRID:
        view = (
          <SocialGrid
            id={id}
            content={content}
            elemProps={elemProps}
            theme={theme}
            layout={layout}
          />
        );
        break;

      case AppConstants.ELEMENT_TYPE_SVG:
        view = <SVG content={content} elemProps={elemProps} theme={theme} layout={layout} />;
        break;

      case AppConstants.ELEMENT_TYPE_ICON:
        view = (
          <Icon content={content} elemProps={elemProps} theme={theme} id={id} layout={layout} />
        );
        break;

      case AppConstants.ELEMENT_TYPE_VIDEO:
        view = <Video content={content} elemProps={elemProps} theme={theme} layout={layout} />;
        break;

      case AppConstants.ELEMENT_TYPE_LOTTIE: {
        // checks parent for grouped autoplay
        const parentSelected = this.props.parent
          ? isSelected(this.props.parent, this.props.layout)
          : false;
        view = (
          <Lottie
            content={content}
            elemProps={elemProps}
            theme={theme}
            layout={layout}
            id={id}
            isSelected={elemSelected || parentSelected}
          />
        );
        break;
      }

      case AppConstants.ELEMENT_TYPE_LINE:
        view = (
          <Line
            content={content}
            elemProps={elemProps.merge(Map({ w: this.state.w }))}
            theme={theme}
            layout={layout}
          />
        );
        break;

      case AppConstants.ELEMENT_TYPE_SIMPLE:
        view = (
          <Simple
            content={content}
            elemProps={elemProps}
            theme={theme}
            layout={layout}
            inheritShadow={style?.boxShadow?.includes("inset")}
          />
        );
        break;

      case AppConstants.ELEMENT_TYPE_IFRAME:
        view = <IFrame content={content} elemProps={elemProps} theme={theme} layout={layout} />;
        break;

      case AppConstants.ELEMENT_TYPE_BUTTON:
        view = (
          <Button
            id={id}
            editing={isEditing}
            element={elem}
            elemProps={elemProps}
            label={elem.get("label")}
            theme={theme}
            layout={layout}
            elemStyle={elemStyle}
            buttonState={buttonState}
          />
        );
        break;

      case AppConstants.ELEMENT_TYPE_FORM_INPUT:
      case AppConstants.ELEMENT_TYPE_FORM_SELECT:
      case AppConstants.ELEMENT_TYPE_FORM_RADIO:
      case AppConstants.ELEMENT_TYPE_FORM_CHECKBOX:
        view = <FormField element={elem} theme={theme} layout={layout} isSelected={elemSelected} />;
        break;

      case DataTypes.GROUP:
        view = <>{[...this._renderGroupChildren()]}</>;
        break;

      default:
        view = null;
    }

    return (
      <div className={cx(classNames)} style={style} data-selected={elemSelected || undefined}>
        {view}
      </div>
    );
  }
}

export default Element;
